import { type ReactNode, useEffect, useState } from 'react'
import { Link } from '@inertiajs/react'
import { ArrowLeft, BarChart3, LayoutDashboard, Megaphone, Pencil, Receipt, Share2, Sparkles } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import CampaignPublishToggle from '@/components/event-form/CampaignPublishToggle'

type CampaignWorkspaceEvent = {
  id: string
  slug: string
  title: string
  date: string | null
  status: 'draft' | 'published'
  page_kind: 'event' | 'fundraiser'
  landing_url?: string | null
}

type CampaignWorkspaceProps = {
  event: CampaignWorkspaceEvent
  title: string
  active: 'overview' | 'analytics' | 'edit' | 'share'
  actions?: ReactNode
  children: ReactNode
}

type RailSection = 'overview' | 'analytics' | 'details' | 'selling' | 'sponsors' | 'appearance' | 'share'

function formatCampaignMeta(event: CampaignWorkspaceEvent) {
  if (event.page_kind === 'fundraiser') return 'Fundraising page'
  if (!event.date) return 'Event page'

  return new Date(`${event.date}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

export default function CampaignWorkspace({ event, title, active, actions, children }: CampaignWorkspaceProps) {
  const campaignType = event.page_kind === 'fundraiser' ? 'Fundraising' : 'Event'
  const [hash, setHash] = useState('')

  useEffect(() => {
    const syncHash = () => setHash(window.location.hash.replace('#', ''))

    syncHash()
    window.addEventListener('hashchange', syncHash)
    return () => window.removeEventListener('hashchange', syncHash)
  }, [])

  const activeRail = getActiveRailSection(active, hash)

  return (
    <div className="min-h-full border-t border-black/5 lg:border-t-0">
      <div className="grid min-h-full lg:grid-cols-[17rem_minmax(0,1fr)]">
        <aside className="border-b border-black/5 bg-muted/20 px-4 py-5 lg:border-r lg:border-b-0">
          <Link
            href="/dashboard/events"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            Campaigns
          </Link>

          <div className="mt-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{campaignType}</p>
            <h2 className="mt-1 font-serif text-2xl font-semibold leading-tight">{event.title}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>{formatCampaignMeta(event)}</span>
              <Badge variant={event.status === 'published' ? 'default' : 'secondary'}>
                {event.status === 'published' ? 'Published' : 'Draft'}
              </Badge>
            </div>
          </div>

          <nav className="mt-6 space-y-5">
            <div>
              <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Setup
              </p>
              <div className="space-y-1">
                <RailLink
                  href={`/dashboard/events/${event.id}`}
                  active={activeRail === 'overview'}
                  icon={<LayoutDashboard className="size-4" />}
                >
                  Overview
                </RailLink>
                <RailLink
                  href={`/dashboard/events/${event.id}/analytics`}
                  active={activeRail === 'analytics'}
                  icon={<BarChart3 className="size-4" />}
                >
                  Engagement
                </RailLink>
                <RailLink
                  href={`/dashboard/events/${event.id}/edit#details`}
                  active={activeRail === 'details'}
                  icon={<Pencil className="size-4" />}
                >
                  Campaign details
                </RailLink>
              </div>
            </div>

            {event.page_kind === 'event' && (
              <div>
                <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Sell
                </p>
                <div className="space-y-1">
                  <RailLink
                    href={`/dashboard/events/${event.id}/edit#selling`}
                    active={activeRail === 'selling'}
                    icon={<Receipt className="size-4" />}
                  >
                    Selling
                  </RailLink>
                  <RailLink
                    href={`/dashboard/events/${event.id}/edit#sponsors`}
                    active={activeRail === 'sponsors'}
                    icon={<Megaphone className="size-4" />}
                  >
                    Sponsors
                  </RailLink>
                </div>
              </div>
            )}

            <div>
              <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Page
              </p>
              <div className="space-y-1">
                <RailLink
                  href={`/dashboard/events/${event.id}/edit#appearance`}
                  active={activeRail === 'appearance'}
                  icon={<Sparkles className="size-4" />}
                >
                  Appearance
                </RailLink>
                {event.status === 'published' && (
                  <RailLink
                    href={`/dashboard/events/${event.id}/share`}
                    active={activeRail === 'share'}
                    icon={<Share2 className="size-4" />}
                  >
                    Share
                  </RailLink>
                )}
              </div>
            </div>
          </nav>
        </aside>

        <section className="min-w-0 px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <h1 className="font-serif text-3xl font-semibold tracking-tight">{title}</h1>
            <div className="flex flex-wrap items-center gap-2">
              <CampaignPublishToggle event={{ id: event.id, status: event.status }} />
              {actions}
            </div>
          </div>
          {children}
        </section>
      </div>
    </div>
  )
}

function getActiveRailSection(active: CampaignWorkspaceProps['active'], hash: string): RailSection {
  if (active === 'overview') return 'overview'
  if (active === 'analytics') return 'analytics'
  if (active === 'share') return 'share'
  if (hash === 'selling') return 'selling'
  if (hash === 'sponsors') return 'sponsors'
  if (hash === 'appearance') return 'appearance'
  return 'details'
}

function RailLink({
  href,
  active,
  icon,
  children,
}: {
  href: string
  active: boolean
  icon: ReactNode
  children: ReactNode
}) {
  return (
    <Link
      href={href}
      onClick={(event) => {
        const url = new URL(href, window.location.href)
        if (url.pathname !== window.location.pathname || !url.hash) return

        event.preventDefault()
        window.history.replaceState(null, '', `${url.pathname}${url.hash}`)
        window.dispatchEvent(new HashChangeEvent('hashchange'))
      }}
      className={`flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition ${
        active
          ? 'bg-primary/10 font-medium text-primary'
          : 'text-foreground/80 hover:bg-black/5 hover:text-foreground'
      }`}
    >
      {icon}
      {children}
    </Link>
  )
}
